import { type User } from '@avanta/shared/types/users';
import { AppointmentService } from 'services/appointments';
import { type SignupResponse } from 'services/auth/types';
import { AppointmentSteps } from 'types/appointments';
import {
  type CategoryType,
  TrackingEventsType,
  UserType,
  UserTypeTranslationMap,
  barometerMap,
  categoryTranslationMap,
} from 'types/tracking';
import { getSpecialityName } from 'utils/tracking';
import { useTracking } from '../useTracking';
import {
  type TrackAppointmentConfirmationParams,
  type TrackDailyPulseCompletedParams,
  type TrackSapaFormSubmittedParams,
  type TrackTherapySessionRatedParams,
  type TrackTrainingSessionAttendedParams,
  type TrackSurveyQuestionAnsweredParams,
  type TrackSurveyCompletedParams,
  type UseTrackingEventsReturn,
} from './types';

export const useTrackingEvents = (): UseTrackingEventsReturn => {
  const { track } = useTracking();

  const trackSignUpCompleted = (data: SignupResponse): void => {
    track(
      TrackingEventsType.USER_SIGN_UP_COMPLETED,
      {
        userId: data.id,
        age: data.age?.toString() ?? '',
        gender: data.gender ?? '',
        campaign: data.campaign ?? '',
        userType: data.type ? UserTypeTranslationMap[data.type] : UserType.PATIENT,
      },
      true
    );
  };

  const trackUserLogin = (user: User): void => {
    track(TrackingEventsType.USER_LOGIN_COMPLETED, {
      userId: user.id,
      userType: UserTypeTranslationMap[user.type ?? ''] || UserType.PATIENT,
    });
  };

  const trackDailyPulseCompleted = ({
    responses,
    survey,
    userId,
  }: TrackDailyPulseCompletedParams): void => {
    const category: CategoryType | null = survey?.tag
      ? (categoryTranslationMap[survey.tag.toLowerCase()] ?? null)
      : null;
    track(TrackingEventsType.DAILY_PULSE_COMPLETED, {
      dailyTrackingScore: responses.join(','),
      userId,
      category,
      ...(survey && { ...survey, tag: undefined, category }),
    });
  };

  const trackAppointmentStep = (
    step: string,
    value: string,
    translateFunction?: (key: string) => string
  ): void => {
    switch (step) {
      case AppointmentSteps.profile:
        track(TrackingEventsType.USER_CONFIGURES_PROFILE, {
          profileData: value,
        });
        break;
      case AppointmentSteps.speciality:
        track(TrackingEventsType.USER_SELECTS_SPECIALITY, {
          selectedSpeciality: getSpecialityName(value, translateFunction),
        });
        break;
      case AppointmentSteps.reasons:
        const translateReason = (reasonId: string): string => {
          if (!reasonId || reasonId.trim() === '') return reasonId;
          const normalizedId = reasonId.toLowerCase().trim();
          return translateFunction
            ? translateFunction(`AppointmentsReserve.steps.reasons.options.${normalizedId}`)
            : reasonId;
        };
        try {
          if (!value || value === '' || value === '[]') {
            track(TrackingEventsType.USER_SELECTS_CONSULTATION_REASON, {
              selectedReason: [],
            });
            break;
          }
          const reasonsArray = JSON.parse(value);
          const translatedReasons =
            Array.isArray(reasonsArray) && reasonsArray.length > 0
              ? reasonsArray
                  .filter((id: string) => id && id.trim() !== '')
                  .map(translateReason)
                  .filter(Boolean)
              : [];
          track(TrackingEventsType.USER_SELECTS_CONSULTATION_REASON, {
            selectedReason: translatedReasons,
          });
        } catch {
          if (value && value !== '' && value.trim() !== '') {
            track(TrackingEventsType.USER_SELECTS_CONSULTATION_REASON, {
              selectedReason: [translateReason(value)],
            });
          } else {
            track(TrackingEventsType.USER_SELECTS_CONSULTATION_REASON, {
              selectedReason: [],
            });
          }
        }
        break;
      case AppointmentSteps.type:
        track(TrackingEventsType.USER_SELECTS_CONSULTATION_TYPE, {
          selectedType: value,
        });
        break;
      case AppointmentSteps.city:
        track(TrackingEventsType.USER_SELECTS_CITY, {
          selectedCity: value,
        });
        break;
      case AppointmentSteps.center:
        (async (): Promise<void> => {
          try {
            const response = await AppointmentService.getCenterDetails(parseInt(value));
            const centerName = response.data.name || value;
            track(TrackingEventsType.USER_SELECTS_CENTER, {
              centerId: value,
              centerName,
            });
          } catch {
            track(TrackingEventsType.USER_SELECTS_CENTER, {
              centerId: value,
              centerName: value,
            });
          }
        })();
        break;
      case AppointmentSteps.schedule:
        track(TrackingEventsType.USER_SELECTS_PREFERRED_SCHEDULE, {
          dataOptions: value,
        });
        break;
      case AppointmentSteps.psychologist:
        const [psychologistId, psychologistName] = value.includes('|')
          ? value.split('|')
          : [value, null];
        track(TrackingEventsType.USER_SELECTS_PROFESSIONAL, {
          psychologistId,
          psychologistName,
          recommendedFlow: false,
        });
        break;
      default:
        break;
    }
  };

  const trackAppointmentConfirmation = (params: TrackAppointmentConfirmationParams): void => {
    track(TrackingEventsType.USER_CONFIRMS_APPOINTMENT, {
      centerId: params.centerId,
      centerName: params.centerName,
      city: params.city,
      psychologistId: params.psychologistId,
      psychologistName: params.psychologistName,
      speciality: params.speciality,
      consultationType: params.consultationType,
      dateTime: params.dateTime,
      consultationReasons: params.consultationReasons,
    });
  };

  const trackTrainingSessionAttended = (params: TrackTrainingSessionAttendedParams): void => {
    track(TrackingEventsType.USER_ATTENDS_TRAINING_SESSION, {
      name: params.name,
      date: params.date,
      category: params.category,
    });
  };
  const trackTherapySessionRated = (params: TrackTherapySessionRatedParams): void => {
    track(TrackingEventsType.THERAPY_SESSION_RATED, {
      psychologistId: params.psychologistId,
      rating: String(params.rate),
      comment: params.comment,
    });
  };

  const trackSurveyQuestionAnswered = (
    params: TrackSurveyQuestionAnsweredParams,
    type?: TrackingEventsType
  ): void => {
    const eventType = type ?? TrackingEventsType.SURVEY_QUESTION_ANSWERED;
    if (!params.category || typeof params.category !== 'string') {
      track(eventType, {
        question: params.question,
        surveyType: params.surveyType,
        category: null,
        answer: params.answer,
        score: params.score ? String(params.score) : null,
        userId: params.userId,
        requiredTextValue: params.requiredTextValue,
      });
      return;
    }

    const normalizedTag = params.category.toLowerCase().trim().replace(/_/g, '-');
    const category = barometerMap[normalizedTag] ?? categoryTranslationMap[normalizedTag] ?? null;

    track(eventType, {
      question: params.question,
      surveyType: params.surveyType,
      category,
      answer: params.answer,
      score: params.score ? String(params.score) : null,
      userId: params.userId,
    });
  };

  const trackSurveyCompleted = (params: TrackSurveyCompletedParams): void => {
    track(TrackingEventsType.SURVEY_COMPLETED, {
      surveyName: params.surveyName,
      surveyScorePoints: String(params.totalScorePoints),
      surveyScorePercentage: String(params.totalScorePercentage),
      emotionalScorePoints: params.emotionalScorePoints
        ? String(params.emotionalScorePoints)
        : null,
      emotionalScorePercentage: params.emotionalScorePercentage
        ? String(params.emotionalScorePercentage)
        : null,
      relationalScorePoints: params.relationalScorePoints
        ? String(params.relationalScorePoints)
        : null,
      relationalScorePercentage: params.relationalScorePercentage
        ? String(params.relationalScorePercentage)
        : null,
      professionalScorePoints: params.professionalScorePoints
        ? String(params.professionalScorePoints)
        : null,
      professionalScorePercentage: params.professionalScorePercentage
        ? String(params.professionalScorePercentage)
        : null,
      healthScorePoints: params.healthScorePoints ? String(params.healthScorePoints) : null,
      healthScorePercentage: params.healthScorePercentage
        ? String(params.healthScorePercentage)
        : null,
    });
  };

  const trackSapaFormSubmitted = (params: TrackSapaFormSubmittedParams): void => {
    track(TrackingEventsType.SAPA_REPORT_SUBMITTED, {
      sapaReportTimestamp: params.timestamp,
      sapaUserData: params.userData as Record<string, unknown>,
      sapaAnswers: params.answers as Record<string, unknown>,
    });
  };

  return {
    trackSignUpCompleted,
    trackUserLogin,
    trackDailyPulseCompleted,
    trackAppointmentStep,
    trackAppointmentConfirmation,
    trackTrainingSessionAttended,
    trackTherapySessionRated,
    trackSurveyQuestionAnswered,
    trackSurveyCompleted,
    trackSapaFormSubmitted,
  };
};
